/**
 * The series detector behind the fill handle, `Ctrl+D`, and `Ctrl+R`
 * (Requirement 12.9).
 *
 * `detectSeries` reads a seed sequence of *typed text* — the same text a keystroke
 * would carry — and classifies it as one of five patterns:
 *
 * | Kind | Seeds | Continuation |
 * | --- | --- | --- |
 * | `constant` | every seed identical | the same text |
 * | `numeric` | numeric literals with a constant difference | last + step |
 * | `date` | date literals with a constant day difference | last + step days |
 * | `text` | a shared prefix and a trailing integer with a constant difference | prefix + next integer |
 * | `cycle` | anything else | the seed block repeated |
 *
 * Two design rules keep this honest.
 *
 * **Detection goes through `classifyInput`.** A seed is numeric exactly when typing
 * it would have produced a number, and a date exactly when typing it would have
 * produced a date. There is no second parser here, so a fill can never disagree
 * with an edit about what `3/5/2024` means.
 *
 * **Continuation is rendered through the same number format.** The step is applied
 * to the stored value and the result is formatted with the format the seeds carried,
 * so a currency series stays currency and a `yyyy-mm-dd` series stays ISO. Because
 * the display engine and the input classifier agree on those layouts, the produced
 * text re-classifies to the value it was produced from.
 *
 * A one-cell seed is a `constant`, which is what makes `Ctrl+D` from a single cell
 * copy it rather than invent a step — Excel's behaviour, and what Requirement 3.1
 * means by "fill down".
 */

import { classifyInput } from '../format/classify.js';
import { formatValue, numberFormat, type NumberFormat } from '../format/numberFormat.js';

/** A detected series, and everything needed to continue it. */
export type Series =
  | { k: 'constant'; text: string }
  | { k: 'numeric'; last: number; step: number; format: NumberFormat }
  | { k: 'date'; last: number; step: number; format: NumberFormat }
  | { k: 'text'; prefix: string; last: number; step: number; width: number }
  | { k: 'cycle'; texts: string[] };

/** How a series is continued past its seeds; `n` is 1-based. */
export function seriesAt(series: Series, n: number): string {
  switch (series.k) {
    case 'constant':
      return series.text;
    case 'numeric':
    case 'date':
      return renderNumber(stepValue(series.last, series.step, n), series.format);
    case 'text': {
      const next = series.last + series.step * n;
      return `${series.prefix}${padInteger(next, series.width)}`;
    }
    case 'cycle': {
      const texts = series.texts;
      if (texts.length === 0) return '';
      return texts[(n - 1) % texts.length] as string;
    }
  }
}

/** The `count` cells that continue `seeds`. */
export function continueSeries(seeds: readonly string[], count: number): string[] {
  const series = detectSeries(seeds);
  const out: string[] = [];
  for (let n = 1; n <= count; n++) out.push(seriesAt(series, n));
  return out;
}

/**
 * Classifies a seed sequence.
 *
 * Order matters: constant is checked first so a repeated number is a constant
 * rather than an arithmetic series of step 0 (they agree, but the report is
 * clearer), then dates before plain numbers because a date literal is also a
 * number, then the text-with-trailing-number pattern, and `cycle` as the fallback
 * that always applies.
 */
export function detectSeries(seeds: readonly string[]): Series {
  if (seeds.length === 0) return { k: 'constant', text: '' };
  const first = seeds[0] as string;
  if (seeds.every((seed) => seed === first)) return { k: 'constant', text: first };
  return detectDate(seeds) ?? detectNumeric(seeds) ?? detectText(seeds) ?? { k: 'cycle', texts: [...seeds] };
}

/* --------------------------------------------------------------- detection */

/** A seed's classified number and the format it carried, or `null`. */
function numericSeed(text: string): { value: number; format: NumberFormat } | null {
  const classified = classifyInput(text);
  if (classified.k !== 'literal') return null;
  if (classified.value.k !== 'number') return null;
  return { value: classified.value.v, format: classified.format };
}

function detectNumeric(seeds: readonly string[]): Series | null {
  const parsed = seeds.map(numericSeed);
  if (parsed.some((p) => p === null)) return null;
  const values = (parsed as { value: number; format: NumberFormat }[]).map((p) => p.value);
  const format = (parsed[0] as { value: number; format: NumberFormat }).format;
  if (format.category === 'date' || format.category === 'time') return null;
  const step = constantStep(values);
  if (step === null) return null;
  return { k: 'numeric', last: values[values.length - 1] as number, step, format };
}

function detectDate(seeds: readonly string[]): Series | null {
  const parsed = seeds.map(numericSeed);
  if (parsed.some((p) => p === null)) return null;
  const typed = parsed as { value: number; format: NumberFormat }[];
  const format = (typed[0] as { value: number; format: NumberFormat }).format;
  if (format.category !== 'date' && format.category !== 'time') return null;
  // A mixed date/time seed block has no single step to continue, so it is not a
  // date series; the fallback repeats it instead.
  if (typed.some((p) => p.format.pattern !== format.pattern)) return null;
  const step = constantStep(typed.map((p) => p.value));
  if (step === null) return null;
  return { k: 'date', last: typed[typed.length - 1]?.value as number, step, format };
}

const TRAILING_NUMBER_RE = /^(.*?)(\d+)$/;

function detectText(seeds: readonly string[]): Series | null {
  const parts: { prefix: string; digits: string }[] = [];
  for (const seed of seeds) {
    // A seed that classifies as a literal is not a text series, whatever it looks
    // like: `2024` is a number, not the prefix-free trailing number of a text run.
    if (classifyInput(seed).k !== 'text') return null;
    const m = TRAILING_NUMBER_RE.exec(seed);
    if (m === null) return null;
    parts.push({ prefix: m[1] as string, digits: m[2] as string });
  }
  const prefix = parts[0]?.prefix as string;
  if (parts.some((p) => p.prefix !== prefix)) return null;
  const values = parts.map((p) => Number(p.digits));
  const step = constantStep(values);
  if (step === null || !Number.isInteger(step)) return null;
  // Zero padding is preserved only when every seed used the same width, so `a08,
  // a09` continues `a10` while `a8, a9` continues `a10` too.
  const widths = parts.map((p) => p.digits.length);
  const padded = parts.every((p) => p.digits.startsWith('0')) && widths.every((w) => w === widths[0]);
  return {
    k: 'text',
    prefix,
    last: values[values.length - 1] as number,
    step,
    width: padded ? (widths[0] as number) : 0,
  };
}

/**
 * The single difference every consecutive pair shares, or `null`.
 *
 * Differences are compared after rounding to the precision the values themselves
 * carry, so `0.1, 0.2, 0.3` is an arithmetic series rather than three unequal
 * binary-floating-point gaps.
 */
function constantStep(values: readonly number[]): number | null {
  if (values.length < 2) return null;
  const step = round((values[1] as number) - (values[0] as number));
  for (let i = 2; i < values.length; i++) {
    if (round((values[i] as number) - (values[i - 1] as number)) !== step) return null;
  }
  return step;
}

/* ------------------------------------------------------------- rendering */

/** Decimal places in a number's shortest decimal form, or 0 for exponent forms. */
function decimalsOf(value: number): number {
  const text = String(value);
  if (text.includes('e') || text.includes('E')) return 0;
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

/** Rounds to 12 places: enough to keep any typed literal, short of float noise. */
function round(value: number): number {
  return Number(value.toFixed(12));
}

/**
 * `last + step * n`, rounded to the precision the seeds carried.
 *
 * Without the rounding, a step of `0.1` produces `0.30000000000000004` on the third
 * cell, which is a different number from the one the user is filling.
 */
function stepValue(last: number, step: number, n: number): number {
  const raw = last + step * n;
  const places = Math.max(decimalsOf(last), decimalsOf(step));
  return places > 0 && places <= 12 ? Number(raw.toFixed(places)) : raw;
}

/** Wide enough that {@link formatValue} never reports an overflow. */
const NO_WIDTH_LIMIT = Number.POSITIVE_INFINITY;

function renderNumber(value: number, format: NumberFormat): string {
  return formatValue({ k: 'number', v: value }, numberFormat(format), NO_WIDTH_LIMIT).text;
}

function padInteger(value: number, width: number): string {
  const text = String(Math.abs(value));
  const sign = value < 0 ? '-' : '';
  return `${sign}${width > 0 ? text.padStart(width, '0') : text}`;
}
